import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import * as React from "react";

import {
  DeselectIcon,
  KeyboardArrowDownIcon,
} from "@/shared/foundation/icons/mui";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";
import { usePopupState } from "@/shared/hooks/usePopupState";

import styles from "./Selection.module.css";

export interface SelectionOption<T> {
  /**
   * Label shown for the menu item. Should be unique per option.
   */
  name: string;

  /**
   * Action fired when the user picks this option. Receives the full
   * accumulated cross-page selection, plus the callback that clears it.
   *
   * Clearing is the consumer's call, not the primitive's: an action that
   * opens a modal returns long before the user confirms or dismisses it.
   * Call `clearSelection` once the action has actually completed; a
   * dismissal needs no handling.
   *
   * Consumers own error handling. The primitive does NOT catch
   * rejections from `action`.
   */
  action: (args: {
    selected: T[];
    clearSelection: () => void;
  }) => void | Promise<void>;

  /** Optional leading icon. */
  icon?: React.ReactNode;
}

export interface SelectionProps<T> {
  /**
   * Current cross-page selection. Sourced from
   * `useResourceView().table.selection` (or `useTable().selection`).
   */
  selection: T[];

  /**
   * Clears the entire selection.
   */
  clearSelection: () => void;

  /**
   * Bulk actions the consumer offers.
   */
  options: SelectionOption<T>[];
}

/**
 * "Selection" trigger + popover menu for the `<ResourceView.Actions>` row.
 * Renders nothing when `options` is empty (consumer hasn't wired any
 * bulk actions) or when `selection` is empty — the button only appears
 * once the user has selected at least one row, so an empty selection
 * never produces a dead affordance in the toolbar.
 */
export function Selection<T>({
  selection,
  clearSelection,
  options,
}: SelectionProps<T>) {
  const popup = usePopupState();

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (popup.isOpen) {
      popup.close();
    } else {
      popup.open({ anchorEl: event.currentTarget });
    }
  };

  const handleDeselectAll = () => {
    popup.close();
    clearSelection();
  };

  const handleOptionClick = async (option: SelectionOption<T>) => {
    popup.close();
    await option.action({ selected: selection, clearSelection });
  };

  if (options.length === 0 || selection.length === 0) {
    return null;
  }

  return (
    <>
      <RobotoButton
        variant="secondary"
        onClick={handleTriggerClick}
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={popup.isOpen}
        data-testid="selection-trigger"
      >
        Selected
        <span className={styles.triggerCount} aria-hidden="true">
          {selection.length}
        </span>
        <KeyboardArrowDownIcon aria-hidden="true" />
      </RobotoButton>
      <Menu
        className={styles.menu}
        anchorEl={popup.anchor}
        open={popup.isOpen}
        onClose={popup.close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={handleDeselectAll} data-testid="selection-deselect">
          <DeselectIcon fontSize="small" />
          Deselect All
        </MenuItem>
        {options.map((option) => (
          <MenuItem
            key={option.name}
            data-testid="selection-option"
            onClick={() => {
              void handleOptionClick(option);
            }}
          >
            {option.icon}
            {option.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
